import { useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { CardFrame } from "../../cards/CardFrame";
import { useFcrCoreMonitor, type FcrCoreMonitorLine } from "../../hooks/useFcrCoreMonitor";

export const MONITOR_CORE_ID = "core";

/** Line prefixes published by modules that call fcr_core.monitor.trace. */
export const MONITOR_STREAM_PREFIX: Record<string, string> = {
  allocated: "allocated",
  occupancy: "occ",
  overall: "fleet",
  bcu34_mvsi: "bcu34_mvsi",
};

const MONITOR_EVENT_NEEDLES: Record<string, string[]> = {
  allocated: ["allocated", "fcr/allocated"],
  occupancy: ["occ", "occupancy", "Occupancy / tx"],
  overall: ["fleet", "Fleet power"],
  bcu34_mvsi: ["bcu34_mvsi", "BCU3/4 MVSI"],
};

export function moduleStreamsMonitor(mod: { id: string; streams_monitor?: boolean }) {
  if (typeof mod.streams_monitor === "boolean") return mod.streams_monitor;
  return mod.id in MONITOR_STREAM_PREFIX;
}

function matchesSource(line: string, sources: string[], kind: "stream" | "events") {
  if (sources.length === 0) return false;
  if (sources.includes(MONITOR_CORE_ID)) return true;
  const hay = line.toLowerCase();
  return sources.some((id) => {
    if (kind === "stream") {
      const prefix = MONITOR_STREAM_PREFIX[id];
      return prefix ? line.startsWith(prefix) : false;
    }
    return (MONITOR_EVENT_NEEDLES[id] ?? [id]).some((needle) => hay.includes(needle.toLowerCase()));
  });
}

const btn =
  "rounded-md border border-border-subtle px-2 py-1 text-text-primary hover:bg-page";

function clock(ts: string) {
  if (!ts) return "--:--:--";
  const slice = ts.includes("T") ? ts.slice(11, 19) : ts.slice(0, 8);
  return slice || ts;
}

const FOLLOW_SLACK_PX = 28;

function lineKey(row: FcrCoreMonitorLine) {
  return `${row.ts}\t${row.line}`;
}

function atBottom(el: HTMLElement) {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
}

function MonitorPane({
  title,
  hint,
  lines,
}: {
  title: string;
  hint: string;
  lines: FcrCoreMonitorLine[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const pinRef = useRef<{ key: string; offset: number } | null>(null);
  const [following, setFollowing] = useState(true);

  function capturePin(el: HTMLElement) {
    const kids = Array.from(el.children) as HTMLElement[];
    for (const child of kids) {
      const key = child.dataset.lineKey;
      if (!key) continue;
      if (child.offsetTop + child.offsetHeight > el.scrollTop) {
        pinRef.current = { key, offset: child.offsetTop - el.scrollTop };
        return;
      }
    }
    pinRef.current = null;
  }

  function onScroll() {
    const el = ref.current;
    if (!el) return;
    const live = atBottom(el);
    followRef.current = live;
    if (live) pinRef.current = null;
    else capturePin(el);
    setFollowing(live);
  }

  function jumpToLive() {
    const el = ref.current;
    followRef.current = true;
    pinRef.current = null;
    setFollowing(true);
    if (el) el.scrollTop = el.scrollHeight;
  }

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (followRef.current) {
      el.scrollTop = el.scrollHeight;
      return;
    }
    const pin = pinRef.current;
    if (!pin) return;
    const child = Array.from(el.children).find(
      (node) => node instanceof HTMLElement && node.dataset.lineKey === pin.key,
    ) as HTMLElement | undefined;
    if (!child) return;
    el.scrollTop = child.offsetTop - pin.offset;
  }, [lines]);

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
          {title}
        </p>
        {following ? (
          <p className="truncate text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
            {hint}
          </p>
        ) : (
          <button
            type="button"
            className="shrink-0 text-accent hover:underline"
            style={{ fontSize: "var(--font-nano)" }}
            onClick={jumpToLive}
          >
            pinned · latest
          </button>
        )}
      </div>
      <div
        ref={ref}
        onScroll={onScroll}
        className="h-56 overflow-auto rounded-md border border-border-subtle bg-page px-2 py-1.5 font-mono text-text-secondary"
        style={{ fontSize: "var(--font-nano)" }}
      >
        {lines.length === 0 ? (
          <span className="text-text-tertiary">waiting…</span>
        ) : (
          lines.map((row) => (
            <div key={lineKey(row)} data-line-key={lineKey(row)} className="whitespace-pre">
              <span className="text-text-tertiary">{clock(row.ts)}  </span>
              {row.line}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function FcrCoreMonitorWindow({
  sources,
  sourceHint,
}: {
  sources: string[];
  sourceHint: string;
}) {
  const { stream, events, connected, error } = useFcrCoreMonitor(true);
  const [paused, setPaused] = useState(false);
  const [capturedStream, setCapturedStream] = useState<FcrCoreMonitorLine[]>([]);
  const [capturedEvents, setCapturedEvents] = useState<FcrCoreMonitorLine[]>([]);
  const frozen = useRef<{ stream: FcrCoreMonitorLine[]; events: FcrCoreMonitorLine[] } | null>(null);
  const sourcesRef = useRef(sources);
  const seenRef = useRef(new Set<string>());
  sourcesRef.current = sources;

  function ingest(
    rows: FcrCoreMonitorLine[],
    kind: "stream" | "events",
    setLog: Dispatch<SetStateAction<FcrCoreMonitorLine[]>>,
  ) {
    const active = sourcesRef.current;
    const seen = seenRef.current;
    const add: FcrCoreMonitorLine[] = [];
    for (const row of rows) {
      const key = `${kind}:${lineKey(row)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      if (matchesSource(row.line, active, kind)) add.push(row);
    }
    if (add.length) setLog((prev) => [...prev, ...add]);
  }

  useEffect(() => {
    ingest(stream, "stream", setCapturedStream);
  }, [stream]);

  useEffect(() => {
    ingest(events, "events", setCapturedEvents);
  }, [events]);

  function togglePause() {
    if (paused) {
      frozen.current = null;
      setPaused(false);
      return;
    }
    frozen.current = { stream: capturedStream, events: capturedEvents };
    setPaused(true);
  }

  const shownStream = paused && frozen.current ? frozen.current.stream : capturedStream;
  const shownEvents = paused && frozen.current ? frozen.current.events : capturedEvents;
  const feeding = sources.length > 0;
  const status = paused ? "paused" : !feeding ? "still" : connected ? "live" : "wait";

  return (
    <CardFrame className="p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="font-medium text-text-primary" style={{ fontSize: "var(--font-body)" }}>
          Monitor
          {sourceHint ? (
            <span className="ml-2 font-normal text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
              {sourceHint}
            </span>
          ) : null}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-text-tertiary" style={{ fontSize: "var(--font-nano)" }}>
            {status}
            {error ? ` · ${error}` : ""}
          </p>
          <button type="button" className={btn} style={{ fontSize: "var(--font-micro)" }} onClick={togglePause}>
            {paused ? "Resume" : "Pause"}
          </button>
        </div>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <MonitorPane title="Stream" hint="fcr/core/monitor · 1 Hz" lines={shownStream} />
        <MonitorPane title="Events" hint="start / stop / reset" lines={shownEvents} />
      </div>
    </CardFrame>
  );
}
